import store from '@/store';
import { Media, MediaObject } from '@awesome-cordova-plugins/media';
import { isPlatform } from '@ionic/vue';
import { MusicControls } from '@awesome-cordova-plugins/music-controls';
import { getDefaultArtworkImage } from '@/helper';

class Player {
  audioElement: HTMLAudioElement;
  currentFile: MediaObject | undefined;
  mediaTimer: any;

  constructor() {
    if (isPlatform('capacitor')) {
      this.registerMediaControlEvents();
    }

    this.audioElement = new Audio();
    this.audioElement.ontimeupdate = () => {
      store.dispatch('setPlayerTimes', {
        currentTime: this.audioElement.currentTime,
        duration: this.audioElement.duration,
      });
    };
    this.audioElement.onended = () => {
      store.dispatch('songEnded');
    };
    this.audioElement.onvolumechange = () => {
      store.commit('setVolume', this.audioElement.volume);
    };
  }

  async setSource(url: string, play = false) {
    if (isPlatform('capacitor')) {
      if (this.currentFile) {
        this.currentFile.release();
      }

      this.currentFile = Media.create(url);
      this.createMediaControls();
      this.currentFile.onStatusUpdate.subscribe((status) => {
        if (status === 2) {
          this.createMediaControls();
        }

        if (status === 4) {
          this.currentFile?.seekTo(0);
          this.stopNativeMediaPoller();
          store.dispatch('songEnded');
        }
      });

      this.currentFile.onError.subscribe((e) => {
        console.log('MEDIA PLAY BACK ERROR!!!');
        console.log(e);
      });

      this.startNativeMediaPoller();
    } else {
      this.audioElement.src = url;
    }

    if (play) this.play();
  }

  createMediaControls() {
    const duration = this.currentFile?.getDuration();
    MusicControls.create({
      track: store.state.player.playingSong.title,
      artist: store.state.player.playingSong.artist || 'Unknown artist',
      album: store.state.player.playingSong.artist || 'Unknown artist',
      cover:
        store.state.player.playingSong.albumArtworkLgUrl ||
        getDefaultArtworkImage(
          store.state.player.playingSong.defaultArtwork,
          'LG',
        ),
      isPlaying: true,
      hasScrubbing: true,
      duration,
    });
  }

  play(url = null) {
    if (url) this.setSource(url);

    if (isPlatform('capacitor')) {
      this.startNativeMediaPoller();
      this.currentFile?.play({ playAudioWhenScreenIsLocked: true });
    } else {
      this.audioElement.play();
    }
  }

  pause() {
    if (isPlatform('capacitor')) {
      this.stopNativeMediaPoller();
      this.currentFile?.pause();
    } else {
      this.audioElement.pause();
    }
  }

  restart() {
    this.pause();
    if (isPlatform('capacitor')) {
      this.currentFile?.seekTo(0);
    } else {
      this.audioElement.currentTime = 0;
    }
    this.play();
  }

  stop() {
    if (isPlatform('capacitor')) {
      this.currentFile?.pause();
      this.stopNativeMediaPoller();
      this.currentFile?.seekTo(0);
    } else {
      this.audioElement.currentTime = 0;
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }

  async seekPercent(percent: number) {
    if (isPlatform('capacitor')) {
      const duration = (await this.currentFile?.getDuration()) || 0.1;
      const seekValue = duration * (percent / 100) * 1000;
      this.currentFile?.seekTo(seekValue);
    } else {
      this.audioElement.currentTime =
        this.audioElement.duration * (percent / 100);
    }
  }

  isUrlExpired(url: string) {
    const urlSearchParams = new URLSearchParams(url);
    const { Expires } = Object.fromEntries(urlSearchParams.entries());
    return parseInt(Expires) < Math.floor(Date.now() / 1000);
  }

  // TODO update this for mobile if we start using the volume controller again
  setVolume(volume: number) {
    this.audioElement.volume = volume;
  }

  // TODO update this for mobile if we start using the volume controller again
  seekVolume(offset: number) {
    const newVolume = this.audioElement.volume + offset;
    if (newVolume > 1) {
      this.audioElement.volume = 1;
    } else if (newVolume < 0) {
      this.audioElement.volume = 0;
    } else {
      this.audioElement.volume = newVolume;
    }
  }

  startNativeMediaPoller() {
    // clear the currenct running one if we need to
    if (this.mediaTimer) {
      clearInterval(this.mediaTimer);
    }

    this.mediaTimer = setInterval(async () => {
      const currPosition = await this.currentFile?.getCurrentPosition();
      const duration = this.currentFile?.getDuration() || 0.01;
      MusicControls.updateElapsed({
        elapsed: Math.floor(currPosition),
        isPlaying: true,
      });
      store.dispatch('setPlayerTimes', {
        currentTime: currPosition,
        duration,
      });
    }, 500);
  }

  stopNativeMediaPoller() {
    clearInterval(this.mediaTimer);
  }

  registerMediaControlEvents() {
    // Register callback
    MusicControls.subscribe().subscribe(async (action) => {
      const message = JSON.parse(action).message;
      const duration = (await this.currentFile?.getDuration()) || 0.1;
      switch (message) {
        case 'music-controls-next':
          console.log('next clicked');
          store.dispatch('playNextSong');
          // Do something
          break;
        case 'music-controls-previous':
          console.log('prev clicked');
          store.dispatch('playPrevSong');
          // Do something
          break;
        case 'music-controls-pause':
          store.dispatch('playPause');
          // Do something
          break;
        case 'music-controls-play':
          store.dispatch('playPause');
          // Do something
          break;
        case 'music-controls-destroy':
          this.stop();
          // Do something
          break;

        // External controls (iOS only)
        case 'music-controls-toggle-play-pause':
          store.dispatch('playPause');
          // Do something
          break;
        case 'music-controls-seek-to':
          store.dispatch(
            'seekPercent',
            (parseFloat(JSON.parse(action).position) / duration) * 100,
          );
          // Do something
          break;

        default:
          break;
      }
    });

    // Start listening for events
    // The plugin will run the events function each time an event is fired
    MusicControls.listen();
  }
}

export const player = new Player();
